import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import {
  Button,
  Dropdown,
  Icon,
  Input,
  Modal,
  ModalSize,
  Table,
  TableColumn,
  TableVariant,
} from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { EmptyState, Loading, PageHeader } from '~/components';
import { NotificationList } from '~/components/NotificationList';
import { RenderFields } from '~/components/RenderFields';
import { RelativeTime } from '~/elements';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { useNavigate } from '~/lib/navigation';
import { CardProgram, CardProgramRepository } from '~/repositories/CardProgramRepository';
import { CreateCardProgramRequest, SimulateRepository } from '~/repositories/SimulateRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import { formatString } from '~/util';

interface CardProgramForm {
  type: CreateCardProgramRequest['type'];
  description: string;
}

const CARD_PROGRAM_TYPE_OPTIONS = [
  { label: 'Debit', value: 'debit' },
  { label: 'Credit', value: 'credit' },
  { label: 'Charge', value: 'charge' },
];

export const PageCardProgramsOverview: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentPlatform, isSandbox } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    cursor,
    hasPrevious,
    onNext,
    onPrevious,
    pageIndex,
    pageSize,
    reset: resetPagination,
    setPageSize,
  } = usePaginationState();

  const isInitialRenderRef = useRef(true);
  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
      return;
    }
    resetPagination();
  }, [currentPlatform?.id, isSandbox, resetPagination]);

  const formMethods = useForm<CardProgramForm>({
    defaultValues: {
      type: 'debit',
      description: '',
    },
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: ['card-programs', 'list', currentPlatform?.id, isSandbox, pageSize, cursor],
    queryFn: () => CardProgramRepository.listCardPrograms({ limit: pageSize, startingAfter: cursor }),
    enabled: Boolean(currentPlatform?.id),
    placeholderData: keepPreviousData,
  });
  const cardPrograms = data?.cardPrograms ?? [];
  const hasMore = Boolean(data?.hasMore);

  const handleOpenModal = useCallback(() => {
    formMethods.reset({ type: 'debit', description: '' });
    setIsModalOpen(true);
  }, [formMethods]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const onSubmit = useCallback(
    async (formData: CardProgramForm) => {
      if (isSubmitting) return;
      setIsSubmitting(true);

      try {
        const request: CreateCardProgramRequest = { type: formData.type };
        if (formData.description.trim()) {
          request.description = formData.description.trim();
        }

        await SimulateRepository.createCardProgram(request);
        handleCloseModal();
        addSuccessNotification({ content: 'Card program created' });
        queryClient.invalidateQueries({ queryKey: ['card-programs'] });
      } catch (err: any) {
        addDangerNotification({ content: err?.message || 'Failed to create card program', display: 'popup' });
      } finally {
        setIsSubmitting(false);
      }
    },
    [isSubmitting, handleCloseModal, addSuccessNotification, addDangerNotification, queryClient]
  );

  const columns: TableColumn<CardProgram>[] = [
    {
      header: 'Name',
      accessor: 'description',
      width: { min: 'min-content', max: '2fr' },
      cell: ({ row }) => <Table.TextCell weight="medium" value={row.description || row.id} />,
    },
    {
      header: 'Network',
      accessor: 'primaryCardScheme',
      width: { min: 'min-content', max: '1fr' },
      cell: ({ value }) => <Table.ChipCell value={value ? formatString(value as string) : '-'} />,
    },
    {
      header: 'BIN',
      accessor: 'bin',
      width: { min: 'min-content', max: '1fr' },
      cell: ({ value }) => <Table.TextCell value={(value as string) || '-'} />,
    },
    {
      header: 'Type',
      accessor: 'cardProgramType',
      width: { min: 'min-content', max: 'min-content' },
      cell: ({ value }) => <Table.ChipCell value={value ? formatString(value as string) : '-'} />,
    },
    {
      header: 'Status',
      accessor: 'status',
      width: { min: 'min-content', max: 'min-content' },
      cell: ({ value }) => <Table.ChipCell value={value ? formatString(value as string) : '-'} />,
    },
    {
      header: 'Created',
      accessor: 'createdAt',
      width: { min: 'min-content', max: 'min-content' },
      cell: ({ value }) => <Table.Cell>{value ? <RelativeTime timestamp={value as Date} /> : '-'}</Table.Cell>,
    },
  ];

  return (
    <>
      <Modal isOpen={isModalOpen} onClose={handleCloseModal} size={ModalSize.Small}>
        <Modal.IconCircle icon="AnimationPiggy" type="primary" />
        <FormProvider {...formMethods}>
          <form onSubmit={formMethods.handleSubmit(onSubmit)}>
            <Modal.Content beforeContent={<NotificationList display="popup" variant="light" />}>
              <Modal.Headline align="center">Create Card Program</Modal.Headline>
              <Modal.Paragraph align="center">Create a sandbox Visa card program</Modal.Paragraph>
              {RenderFields<CardProgramForm>({
                sections: [
                  {
                    variant: 'isCompact',
                    fields: [
                      {
                        id: 'type',
                        label: 'Type',
                        children: ({ value, onChange }) => (
                          <Dropdown
                            active={String(value)}
                            onChange={onChange}
                            options={CARD_PROGRAM_TYPE_OPTIONS}
                            fullWidth
                          />
                        ),
                      },
                      {
                        id: 'description',
                        label: 'Description',
                        children: ({ value, onChange }) => (
                          <Input value={String(value)} onChange={onChange} placeholder="Description (optional)" />
                        ),
                      },
                    ],
                  },
                ],
              })}
            </Modal.Content>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal} size="small" type="button">
                Cancel
              </Button>
              <Button isLoading={isSubmitting} isDisabled={isSubmitting} size="small" type="submit">
                Create Program
              </Button>
            </Modal.Footer>
          </form>
        </FormProvider>
      </Modal>

      <PageHeader text="Card Programs" border={false}>
        {isSandbox && (
          <Button onClick={handleOpenModal} size="small" icon={<Icon.Sandbox />}>
            Create Program
          </Button>
        )}
      </PageHeader>
      <Loading isLoading={isLoading}>
        <Inner pt={0} px="var(--page-margin)">
          {isError ? (
            <EmptyState headline="Error" text="Failed to load card programs." />
          ) : cardPrograms.length === 0 && !hasPrevious ? (
            <EmptyState headline="No card programs" text="No card programs found." />
          ) : (
            <Table<CardProgram>
              columns={columns}
              data={cardPrograms}
              variant={TableVariant.List}
              pagination={{
                hasNext: hasMore,
                hasPrevious,
                pageIndex,
                pageSize,
                onNext: () => {
                  if (cardPrograms.length > 0) {
                    onNext({ cursor: cardPrograms[cardPrograms.length - 1].id });
                  }
                },
                onPrevious,
                onPageSizeChange: setPageSize,
              }}
              onRowClick={(row) => {
                navigate(`${ROUTE.CARD_PROGRAMS}/edit/${row.id}`);
              }}
            />
          )}
        </Inner>
      </Loading>
    </>
  );
};
